import { useEffect, useState } from "react";
import { Button } from "components/ui/button";
import { Progress } from "components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import {
  SpinnerGap,
  CheckCircle,
  XCircle,
  Warning,
  X,
} from "@phosphor-icons/react";
import { BulkAnalysisJob, BulkJobStatus } from "shared/hooks/useDocumentAnalysis";

interface BulkAnalysisProgressProps {
  job: BulkAnalysisJob | null;
  onCancel: () => void;
  onClose: () => void;
  onRefreshStatus: () => Promise<void>;
  isPolling?: boolean;
}

const BulkAnalysisProgress = ({
  job,
  onCancel,
  onClose,
  onRefreshStatus,
}: BulkAnalysisProgressProps) => {
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Auto-poll for status updates
  useEffect(() => {
    if (job && (job.status === BulkJobStatus.PENDING || job.status === BulkJobStatus.PROCESSING)) {
      const interval = setInterval(() => {
        onRefreshStatus();
      }, 3000); // Poll every 3 seconds
      setPollInterval(interval);

      return () => {
        clearInterval(interval);
      };
    } else if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [job?.status]);

  if (!job) return null;

  const progressPercent = job.totalDocuments > 0
    ? Math.round((job.processedDocuments / job.totalDocuments) * 100)
    : 0;

  const isCompleted = job.status === BulkJobStatus.COMPLETED;
  const isFailed = job.status === BulkJobStatus.FAILED;
  const isCancelled = job.status === BulkJobStatus.CANCELLED;
  const isProcessing = job.status === BulkJobStatus.PROCESSING || job.status === BulkJobStatus.PENDING;

  const getStatusIcon = () => {
    if (isCompleted) return <CheckCircle className="w-6 h-6 text-moss-primary" />;
    if (isFailed) return <XCircle className="w-6 h-6 text-brick-primary" />;
    if (isCancelled) return <Warning className="w-6 h-6 text-honey-primary" />;
    return <SpinnerGap className="w-6 h-6 text-moss-primary animate-spin" />;
  };

  const getStatusText = () => {
    switch (job.status) {
      case BulkJobStatus.PENDING:
        return "Voorbereiden...";
      case BulkJobStatus.PROCESSING:
        return "Documenten analyseren...";
      case BulkJobStatus.COMPLETED:
        return "Analyse voltooid";
      case BulkJobStatus.FAILED:
        return "Analyse mislukt";
      case BulkJobStatus.CANCELLED:
        return "Analyse geannuleerd";
      default:
        return "Onbekende status";
    }
  };

  return (
    <Card className="border-2 border-moss-primary/20 bg-moss-primary/5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <CardTitle className="text-base">{getStatusText()}</CardTitle>
          </div>
          {!isProcessing && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <Progress value={progressPercent} className="h-2" />
          <div className="flex justify-between text-sm text-neutral-secondary-mid">
            <span>
              {job.processedDocuments} / {job.totalDocuments} documenten
            </span>
            <span>{progressPercent}%</span>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-semibold text-neutral-primary">
              {job.totalDocuments}
            </div>
            <div className="text-xs text-neutral-secondary-mid">Totaal</div>
          </div>
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-semibold text-moss-primary">
              {job.successfulDocuments}
            </div>
            <div className="text-xs text-neutral-secondary-mid">Geslaagd</div>
          </div>
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-semibold text-brick-primary">
              {job.failedDocuments}
            </div>
            <div className="text-xs text-neutral-secondary-mid">Mislukt</div>
          </div>
          <div className="bg-white rounded-lg p-2">
            <div className="text-lg font-semibold text-neutral-400">
              {job.skippedDocuments}
            </div>
            <div className="text-xs text-neutral-secondary-mid">Overgeslagen</div>
          </div>
        </div>

        {/* Errors list */}
        {job.errors && job.errors.length > 0 && (
          <div className="bg-brick-primary/5 border border-brick-primary/20 rounded-lg p-3 max-h-32 overflow-y-auto">
            <p className="text-sm font-medium text-brick-primary mb-2">Fouten:</p>
            <ul className="space-y-1">
              {job.errors.slice(0, 5).map((error, idx) => (
                <li key={idx} className="text-xs text-neutral-secondary-mid">
                  <span className="font-mono">#{error.fileLogId}</span>: {error.error}
                </li>
              ))}
              {job.errors.length > 5 && (
                <li className="text-xs text-neutral-secondary-mid italic">
                  ...en {job.errors.length - 5} meer fouten
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Processing time */}
        {job.totalProcessingTimeMs && (
          <div className="text-sm text-neutral-secondary-mid text-center">
            Totale verwerkingstijd: {Math.round(job.totalProcessingTimeMs / 1000)} seconden
          </div>
        )}

        {/* Action buttons */}
        {isProcessing && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={onCancel} className="text-brick-primary border-brick-primary hover:bg-brick-primary/10">
              Annuleren
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkAnalysisProgress;
